import {
  AlertTriangle,
  ArrowRightLeft,
  ArrowLeftRight,
  BadgePercent,
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  ClipboardCheck,
  CalendarClock,
  Clock,
  Coins,
  CreditCard,
  FileText,
  FolderTree,
  Handshake,
  HelpCircle,
  History,
  LayoutDashboard,
  Landmark,
  LineChart,
  Package,
  PackageCheck,
  Settings,
  ShoppingCart,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Store,
  Send,
  Tag,
  Truck,
  Upload,
  UserCircle,
  Users,
  Wallet,
  Warehouse,
  Wine,
  BedDouble,
  type LucideIcon,
  Inbox,
  FlaskConical,
} from 'lucide-react';

export interface NavChild {
  labelKey: string;
  href: string;
  permission?: string;
  /** Lucide icon rendered in V3 flyout panels (left of the child label). */
  icon?: LucideIcon;
  /**
   * Optional shop feature flag that gates this entry. When set, the rail sidebar
   * only shows this child when the active shop has the flag enabled.
   */
  featureFlag?:
    | 'drivers_enabled'
    | 'returns_enabled'
    | 'bar_mode_enabled'
    | 'ingredient_management_enabled'
    | 'partner_mode_enabled'
    | 'commerce_pages_enabled'
    | 'tracked_stock_transfers_v1'
    | 'rental_mode_enabled';
}

export interface NavItem {
  icon: LucideIcon;
  labelKey: string;
  href: string;
  children?: NavChild[];
  permission?: string;
  featureFlag?:
    | 'drivers_enabled'
    | 'returns_enabled'
    | 'bar_mode_enabled'
    | 'ingredient_management_enabled'
    | 'partner_mode_enabled'
    | 'commerce_pages_enabled'
    | 'tracked_stock_transfers_v1'
    | 'rental_mode_enabled';
}

export const shopNavItems: NavItem[] = [
  {
    icon: LayoutDashboard,
    labelKey: 'dashboard',
    href: '/dashboard',
    permission: 'reports.view',
  },
  {
    icon: ShoppingCart,
    labelKey: 'sales',
    href: '/sales',
    permission: 'sales.view',
    children: [
      { labelKey: 'history', href: '/sales', permission: 'sales.view' },
      { labelKey: 'statistics', href: '/sales/statistics', permission: 'reports.view' },
      { labelKey: 'sales_approvals', href: '/sales/approvals', permission: 'sales.approve' },
      { labelKey: 'customers', href: '/customers', permission: 'customers.view' },
      {
        labelKey: 'drivers',
        href: '/drivers',
        permission: 'employees.view',
        featureFlag: 'drivers_enabled',
      },
    ],
  },
  {
    icon: ArrowRightLeft,
    labelKey: 'returns',
    href: '/returns',
    permission: 'returns.view',
    children: [
      {
        labelKey: 'returns_list',
        href: '/returns',
        permission: 'returns.view',
        featureFlag: 'returns_enabled',
      },
      {
        labelKey: 'returns_approvals',
        href: '/returns/approvals',
        permission: 'returns.approve',
        featureFlag: 'returns_enabled',
      },
    ],
  },
  {
    icon: LineChart,
    labelKey: 'stock',
    href: '/stock',
    permission: 'stock.view',
    children: [
      { labelKey: 'stock_overview', href: '/stock', permission: 'stock.view' },
      { labelKey: 'movements', href: '/stock/movements', permission: 'stock.view' },
      {
        labelKey: 'tracked_stock_transfers',
        href: '/stock/transfers',
        permission: 'stock.view',
        featureFlag: 'tracked_stock_transfers_v1',
      },
      { labelKey: 'alerts', href: '/stock/alerts', permission: 'stock.view' },
      { labelKey: 'inventory_stocktake', href: '/inventory/stocktake', permission: 'stock.view' },
      { labelKey: 'inventory_history', href: '/inventory/history', permission: 'stock.view' },
      { labelKey: 'warehouses', href: '/stock/warehouses', permission: 'warehouses.view' },
    ],
  },
  {
    icon: Package,
    labelKey: 'products',
    href: '/products',
    permission: 'products.view',
    children: [
      { labelKey: 'product_list', href: '/products', permission: 'products.view' },
      {
        labelKey: 'product_ingredients',
        href: '/products/ingredients',
        permission: 'products.view',
        featureFlag: 'ingredient_management_enabled',
      },
      { labelKey: 'categories', href: '/categories', permission: 'categories.view' },
      { labelKey: 'brands', href: '/brands', permission: 'brands.view' },
      { labelKey: 'attributes', href: '/attributes', permission: 'products.view' },
      { labelKey: 'promotions', href: '/promotions', permission: 'products.view' },
      { labelKey: 'import_export', href: '/products/import', permission: 'products.*' },
    ],
  },
  {
    icon: Store,
    labelKey: 'commerce_pages',
    href: '/commerce/orders',
    permission: 'products.view',
    featureFlag: 'commerce_pages_enabled',
    children: [
      {
        labelKey: 'commerce_orders',
        href: '/commerce/orders',
        permission: 'sales.view',
        icon: Inbox,
      },
      {
        labelKey: 'commerce_reservations',
        href: '/commerce/reservations',
        permission: 'sales.view',
        icon: CalendarClock,
      },
      {
        labelKey: 'commerce_pages_list',
        href: '/commerce',
        permission: 'products.view',
        icon: Store,
      },
      {
        labelKey: 'commerce_withdrawals',
        href: '/commerce/withdrawals',
        permission: 'sales.view',
        icon: Wallet,
      },
    ],
  },
  {
    icon: BedDouble,
    labelKey: 'rental',
    href: '/rental',
    permission: 'rental.reservations',
    featureFlag: 'rental_mode_enabled',
  },
  {
    icon: Sparkles,
    labelKey: 'ai_analytics',
    href: '/ai',
    permission: 'reports.view',
  },
  {
    icon: Landmark,
    labelKey: 'finances',
    href: '/finances',
    permission: 'finances.access',
    children: [
      {
        labelKey: 'finances_overview',
        href: '/finances',
        permission: 'accounting.read',
        icon: BarChart3,
      },
      {
        labelKey: 'finances_purchases',
        href: '/finances/achats',
        permission: 'accounting.read',
        icon: FileText,
      },
      {
        labelKey: 'finances_payables',
        href: '/finances/a-payer',
        permission: 'accounting.read',
        icon: Clock,
      },
      {
        labelKey: 'finances_sessions',
        href: '/finances/sessions',
        permission: 'reports.view',
        icon: ClipboardCheck,
      },
      {
        labelKey: 'finances_reconciliation',
        href: '/finances/rapprochement',
        permission: 'accounting.reconcile',
        icon: ArrowLeftRight,
      },
      {
        labelKey: 'finances_journal',
        href: '/finances/journal',
        permission: 'accounting.read',
        icon: BookOpen,
      },
      {
        labelKey: 'finances_exports',
        href: '/finances/exports',
        permission: 'accounting.export',
        icon: Upload,
      },
      {
        labelKey: 'finances_settings',
        href: '/finances/reglages',
        permission: 'accounting.read',
        icon: Settings,
      },
    ],
  },
  {
    icon: Users,
    labelKey: 'employees',
    href: '/employees',
    permission: 'employees.view',
    children: [
      { labelKey: 'employee_list', href: '/employees', permission: 'employees.view' },
      { labelKey: 'permissions', href: '/employees/permissions', permission: 'employees.*' },
    ],
  },
  { icon: Store, labelKey: 'shops', href: '/shops' },
];

export const accountNavItems: NavItem[] = [
  {
    icon: CreditCard,
    labelKey: 'subscription',
    href: '/subscription',
    permission: 'subscriptions.view',
    children: [
      { labelKey: 'current_plan', href: '/subscription', permission: 'subscriptions.view' },
      {
        labelKey: 'billing_history',
        href: '/subscription/history',
        permission: 'subscriptions.view',
      },
      {
        labelKey: 'ai_credits',
        href: '/ai/credits',
        permission: 'subscriptions.view',
        icon: Coins,
      },
    ],
  },
  {
    icon: Settings,
    labelKey: 'settings',
    href: '/settings',
    permission: 'settings.view',
    children: [
      { labelKey: 'profile', href: '/settings/profile' },
      { labelKey: 'shop_settings', href: '/settings/shop', permission: 'settings.*' },
      { labelKey: 'devices', href: '/settings/devices', permission: 'settings.*' },
      { labelKey: 'ai_connections', href: '/settings/ai-connections', permission: 'settings.*' },
      { labelKey: 'notifications', href: '/settings/notifications' },
      { labelKey: 'preferences', href: '/settings/preferences' },
    ],
  },
  {
    icon: BookOpen,
    labelKey: 'guide',
    href: '/guide',
  },
  {
    icon: HelpCircle,
    labelKey: 'support',
    href: '/support',
  },
];

/**
 * Dashboard V3 (HeyGen-inspired) regroups operational sections into 6 top-level
 * items. "Compte" aggregates admin/account routes that previously lived in the
 * separate accountNavItems list.
 *
 * Used by `components/dashboard-v3/Sidebar.tsx`. Keep `shopNavItems` and
 * `accountNavItems` unchanged so the V2 RailSidebar continues to work during
 * the feature-flag migration.
 */
export const dashboardV3NavItems: NavItem[] = [
  // NOTE Phase 8 — "dashboard" is intentionally NOT here. The Velko logo at the
  // top of the rail navigates to /dashboard AND opens a popover with the shop
  // switcher. Keeping a redundant "Dashboard" entry would clutter the rail.
  {
    icon: ShoppingCart,
    labelKey: 'sales',
    href: '/sales',
    permission: 'sales.view',
    children: [
      { labelKey: 'history', href: '/sales', permission: 'sales.view', icon: Clock },
      {
        labelKey: 'statistics',
        href: '/sales/statistics',
        permission: 'reports.view',
        icon: BarChart3,
      },
      {
        labelKey: 'sales_commissions',
        href: '/reports/commissions',
        permission: 'reports.view',
        icon: BadgePercent,
      },
      {
        labelKey: 'sales_approvals',
        href: '/sales/approvals',
        permission: 'sales.approve',
        icon: ClipboardCheck,
      },
      { labelKey: 'customers', href: '/customers', permission: 'customers.view', icon: Users },
      {
        labelKey: 'drivers',
        href: '/drivers',
        permission: 'employees.view',
        featureFlag: 'drivers_enabled',
        icon: Truck,
      },
      {
        labelKey: 'deliveries',
        href: '/deliveries',
        permission: 'sales.view',
        featureFlag: 'drivers_enabled',
        icon: Send,
      },
    ],
  },
  {
    icon: ArrowRightLeft,
    labelKey: 'returns',
    href: '/returns',
    permission: 'returns.view',
    featureFlag: 'returns_enabled',
    children: [
      {
        labelKey: 'returns_list',
        href: '/returns',
        permission: 'returns.view',
        featureFlag: 'returns_enabled',
        icon: ArrowRightLeft,
      },
      {
        labelKey: 'returns_approvals',
        href: '/returns/approvals',
        permission: 'returns.approve',
        featureFlag: 'returns_enabled',
        icon: ClipboardCheck,
      },
    ],
  },
  {
    icon: LineChart,
    labelKey: 'stock',
    href: '/stock',
    permission: 'stock.view',
    children: [
      { labelKey: 'stock_overview', href: '/stock', permission: 'stock.view', icon: Boxes },
      {
        labelKey: 'movements',
        href: '/stock/movements',
        permission: 'stock.view',
        icon: ArrowLeftRight,
      },
      {
        labelKey: 'tracked_stock_transfers',
        href: '/stock/transfers',
        permission: 'stock.view',
        featureFlag: 'tracked_stock_transfers_v1',
        icon: ArrowRightLeft,
      },
      { labelKey: 'alerts', href: '/stock/alerts', permission: 'stock.view', icon: AlertTriangle },
      {
        labelKey: 'inventory_stocktake',
        href: '/inventory/stocktake',
        permission: 'stock.view',
        icon: ClipboardCheck,
      },
      {
        labelKey: 'inventory_history',
        href: '/inventory/history',
        permission: 'stock.view',
        icon: History,
      },
      {
        labelKey: 'warehouses',
        href: '/stock/warehouses',
        permission: 'warehouses.view',
        icon: Warehouse,
      },
    ],
  },
  {
    icon: Package,
    labelKey: 'products',
    href: '/products',
    permission: 'products.view',
    children: [
      { labelKey: 'product_list', href: '/products', permission: 'products.view', icon: Package },
      {
        labelKey: 'product_ingredients',
        href: '/products/ingredients',
        permission: 'products.view',
        featureFlag: 'ingredient_management_enabled',
        icon: FlaskConical,
      },
      {
        labelKey: 'categories',
        href: '/categories',
        permission: 'categories.view',
        icon: FolderTree,
      },
      { labelKey: 'brands', href: '/brands', permission: 'brands.view', icon: Tag },
      {
        labelKey: 'attributes',
        href: '/attributes',
        permission: 'products.view',
        icon: SlidersHorizontal,
      },
      {
        labelKey: 'promotions',
        href: '/promotions',
        permission: 'products.view',
        icon: BadgePercent,
      },
      {
        labelKey: 'import_export',
        href: '/products/import',
        permission: 'products.*',
        icon: Upload,
      },
    ],
  },
  // Canaux de vente (Commandes en ligne, vitrines publiques, réservations) — opt-in par boutique.
  {
    icon: Store,
    labelKey: 'commerce_pages',
    href: '/commerce/orders',
    permission: 'products.view',
    featureFlag: 'commerce_pages_enabled',
    children: [
      {
        labelKey: 'commerce_orders',
        href: '/commerce/orders',
        permission: 'sales.view',
        icon: Inbox,
      },
      {
        labelKey: 'commerce_reservations',
        href: '/commerce/reservations',
        permission: 'sales.view',
        icon: CalendarClock,
      },
      {
        labelKey: 'commerce_pages_list',
        href: '/commerce',
        permission: 'products.view',
        icon: Store,
      },
      {
        labelKey: 'commerce_withdrawals',
        href: '/commerce/withdrawals',
        permission: 'sales.view',
        icon: Wallet,
      },
    ],
  },
  {
    icon: Landmark,
    labelKey: 'finances',
    href: '/finances',
    permission: 'finances.access',
    children: [
      {
        labelKey: 'finances_overview',
        href: '/finances',
        permission: 'accounting.read',
        icon: BarChart3,
      },
      {
        labelKey: 'finances_purchases',
        href: '/finances/achats',
        permission: 'accounting.read',
        icon: FileText,
      },
      {
        labelKey: 'finances_payables',
        href: '/finances/a-payer',
        permission: 'accounting.read',
        icon: Clock,
      },
      {
        labelKey: 'finances_sessions',
        href: '/finances/sessions',
        permission: 'reports.view',
        icon: ClipboardCheck,
      },
      {
        labelKey: 'finances_reconciliation',
        href: '/finances/rapprochement',
        permission: 'accounting.reconcile',
        icon: ArrowLeftRight,
      },
      {
        labelKey: 'finances_journal',
        href: '/finances/journal',
        permission: 'accounting.read',
        icon: BookOpen,
      },
      {
        labelKey: 'finances_exports',
        href: '/finances/exports',
        permission: 'accounting.export',
        icon: Upload,
      },
      {
        labelKey: 'finances_settings',
        href: '/finances/reglages',
        permission: 'accounting.read',
        icon: Settings,
      },
    ],
  },
  {
    icon: BedDouble,
    labelKey: 'rental',
    href: '/rental',
    permission: 'rental.reservations',
    featureFlag: 'rental_mode_enabled',
  },
  {
    icon: Wine,
    labelKey: 'bar',
    href: '/bar/ingredients',
    featureFlag: 'bar_mode_enabled',
    children: [
      {
        labelKey: 'bar_ingredients',
        href: '/bar/ingredients',
        featureFlag: 'bar_mode_enabled',
        icon: Boxes,
      },
      {
        labelKey: 'bar_recipes',
        href: '/bar/recipes',
        featureFlag: 'bar_mode_enabled',
        icon: BookOpen,
      },
      {
        labelKey: 'bar_stocktake',
        href: '/bar/stocktake',
        featureFlag: 'bar_mode_enabled',
        icon: ClipboardCheck,
      },
      {
        labelKey: 'bar_consumption',
        href: '/bar/consumption',
        featureFlag: 'bar_mode_enabled',
        icon: History,
      },
    ],
  },
  {
    icon: Handshake,
    labelKey: 'partners',
    href: '/partners',
    featureFlag: 'partner_mode_enabled',
    children: [
      {
        labelKey: 'partners_list',
        href: '/partners',
        featureFlag: 'partner_mode_enabled',
        icon: Users,
      },
      {
        labelKey: 'partners_consignments',
        href: '/partners/consignments',
        featureFlag: 'partner_mode_enabled',
        icon: PackageCheck,
      },
      {
        labelKey: 'partners_settlements',
        href: '/partners/settlements',
        featureFlag: 'partner_mode_enabled',
        icon: Wallet,
      },
    ],
  },

  {
    icon: Users,
    labelKey: 'account',
    href: '/employees',
    children: [
      { labelKey: 'employees', href: '/employees', permission: 'employees.view', icon: Users },
      { labelKey: 'shops', href: '/shops', icon: Store },
      { labelKey: 'ai_analytics', href: '/ai', permission: 'reports.view', icon: Sparkles },
      {
        labelKey: 'ai_credits',
        href: '/ai/credits',
        permission: 'subscriptions.view',
        icon: Coins,
      },
      {
        labelKey: 'subscription',
        href: '/subscription',
        permission: 'subscriptions.view',
        icon: CreditCard,
      },
      { labelKey: 'guide', href: '/guide', icon: BookOpen },
      { labelKey: 'support', href: '/support', icon: HelpCircle },
    ],
  },
  {
    icon: Settings,
    labelKey: 'settings',
    href: '/settings',
    permission: 'settings.view',
    children: [
      { labelKey: 'profile', href: '/settings/profile', icon: UserCircle },
      { labelKey: 'shop_settings', href: '/settings/shop', permission: 'settings.*', icon: Store },
      {
        labelKey: 'devices',
        href: '/settings/devices',
        permission: 'settings.*',
        icon: Smartphone,
      },
      {
        labelKey: 'ai_connections',
        href: '/settings/ai-connections',
        permission: 'settings.*',
        icon: Sparkles,
      },
      { labelKey: 'notifications', href: '/settings/notifications', icon: Bell },
      { labelKey: 'preferences', href: '/settings/preferences', icon: SlidersHorizontal },
    ],
  },
];
